import { Controller } from "@hotwired/stimulus"

/**
 * Manages the bid calculator report table interactions:
 * - Opening the primary conversions drawer with per-item content
 * - Handling bid type dropdown changes
 * - Closing the drawer after primary conversions are saved
 */
export default class extends Controller {
  static targets = ["drawerFrame"]
  static values = { itemsUrl: String }

  connect() {
    this.handleSubmitEnd = this.#closeDrawerAfterSubmit.bind(this)
    document.addEventListener("turbo:submit-end", this.handleSubmitEnd)
    this._bufferTimers = {}
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.handleSubmitEnd)
    Object.values(this._bufferTimers).forEach(clearTimeout)
  }

  openPrimaryDrawer(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.drawerUrl
    if (!url) return

    // Open the drawer first, then load content into the frame
    window.dispatchEvent(
      new CustomEvent("drawer:open", {
        detail: { instance: "primary-conversions" }
      })
    )

    if (this.hasDrawerFrameTarget) {
      this.drawerFrameTarget.src = url
    }
  }

  bidTypeChanged(event) {
    const itemId = event.currentTarget.dataset.itemId
    const bidding_strategy = event.currentTarget.value
    this.#saveItem(itemId, { bidding_strategy }, { acceptTurboStream: true })
  }

  bufferChanged(event) {
    const itemId = event.currentTarget.dataset.itemId
    const buffer = parseFloat(event.currentTarget.value) || 0

    // Update +/- buffer display immediately
    this.#recalcBufferDisplay(itemId, buffer)

    // Debounced AJAX save (500ms)
    if (this._bufferTimers[itemId]) clearTimeout(this._bufferTimers[itemId])
    this._bufferTimers[itemId] = setTimeout(() => {
      this.#saveItem(itemId, { buffer_percentage: buffer })
    }, 500)
  }

  #recalcBufferDisplay(itemId, buffer) {
    const impliedCell = this.element.querySelector(`[data-item-implied-target="${itemId}"]`)
    const plusCell = this.element.querySelector(`[data-item-buffer-plus="${itemId}"]`)
    const minusCell = this.element.querySelector(`[data-item-buffer-minus="${itemId}"]`)
    if (!impliedCell) return

    const rawText = impliedCell.textContent.trim()
    const implied = parseFloat(rawText.replace(/[$,]/g, ""))
    if (isNaN(implied)) return

    const isCurrency = rawText.startsWith("$")
    const fmt = (val) => isCurrency ? `$${val.toFixed(2)}` : val.toFixed(2)

    if (plusCell) plusCell.textContent = fmt(implied * (1 + buffer / 100))
    if (minusCell) minusCell.textContent = fmt(implied * (1 - buffer / 100))
  }

  async #saveItem(itemId, attrs, { acceptTurboStream = false } = {}) {
    if (!this.hasItemsUrlValue) return
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const accept = acceptTurboStream ? "text/vnd.turbo-stream.html" : "application/json"

    this.#setRowSaving(itemId, true)

    try {
      const res = await fetch(this.itemsUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": accept
        },
        body: JSON.stringify({ bid_calculator_items: { [itemId]: attrs } })
      })

      if (!res.ok) {
        console.warn("Save failed:", res.status)
      } else if (acceptTurboStream) {
        Turbo.renderStreamMessage(await res.text())
      }
    } catch (e) {
      console.error("Failed to save item:", e)
    } finally {
      this.#setRowSaving(itemId, false)
    }
  }

  #setRowSaving(itemId, saving) {
    const row = document.getElementById(`bid_calculator_item_${itemId}`)
    if (!row) return
    row.classList.toggle("opacity-50", saving)
    row.classList.toggle("pointer-events-none", saving)
  }

  // --- private ---

  #closeDrawerAfterSubmit(event) {
    // Only close if the submission came from within the primary conversions drawer
    const form = event.target
    const drawerEl = document.getElementById("primary-conversions-drawer")
    if (!drawerEl || !drawerEl.contains(form)) return
    if (!event.detail.success) return

    // Find the drawer-instance close button and click it
    const closeBtn = drawerEl.querySelector("[data-action*='drawer-instance#close']")
    if (closeBtn) {
      closeBtn.click()
    }
  }
}
